import React from 'react'
import { Box, Popper } from '@mui/material'
import { AisleCell, GridCell } from '../types'
import { CellTooltipContent } from './CellTooltipContent'
import { POPPER_SX, TOOLTIP_SX } from '../styles/CellTooltipContent.styles'
import { OpenCell } from '../hooks/useCellSummary'

/**
 * Constante de módulo, no literal inline: popper.js reacciona a la identidad
 * del array y con un literal se reconfiguraba en cada render del padre.
 */
const POPPER_MODIFIERS = [
  { name: 'offset', options: { offset: [0, 8] } },
  { name: 'preventOverflow', options: { padding: 8 } },
]

/**
 * Resumen de la celda seleccionada.
 *
 * Uno solo por grilla, anclado a la celda que recibió el click. Reemplaza al
 * `Tooltip` que antes montaba cada celda: como por diseño sólo puede haber un
 * resumen visible, tener más de mil instancias de Tooltip era costo puro.
 *
 * `Popper` renderiza en un portal a `document.body`, así que no lo recorta el
 * contenedor con scroll, y se reposiciona solo al desplazar el mapa.
 *
 * Va `keepMounted` a propósito. Sin eso, cada apertura tiene que crear el nodo,
 * portarlo, instanciar popper.js y correr `preventOverflow`, que mide los
 * ancestros que recortan — y acá el ancestro es un contenedor con scroll y más
 * de mil celdas, o sea un reflow forzado sobre un subárbol grande. Con un único
 * Popper el costo de dejarlo montado es un div oculto; con los 1.500 Tooltip de
 * antes habría sido impensable.
 */
export const CellSummaryPopper = React.memo(function CellSummaryPopper({
  openCell,
  cell,
  rowLabel,
  brandColors,
  zoneColors,
  showDetailHint = false,
}: {
  openCell: OpenCell | null
  cell: GridCell | AisleCell | undefined
  rowLabel: string
  brandColors: Map<string, string>
  zoneColors?: Map<string, string>
  showDetailHint?: boolean
}) {
  const isOpen = openCell !== null && cell !== undefined

  return (
    <Popper
      open={isOpen}
      anchorEl={openCell?.anchorEl ?? null}
      placement='top'
      keepMounted
      sx={POPPER_SX}
      modifiers={POPPER_MODIFIERS}
    >
      {cell && (
        <Box sx={TOOLTIP_SX}>
          <CellTooltipContent
            cell={cell}
            rowLabel={rowLabel}
            brandColors={brandColors}
            zoneColors={zoneColors}
            showDetailHint={showDetailHint}
          />
        </Box>
      )}
    </Popper>
  )
})
